import boostSchema from 'schemas/boost';
import postSchema, { getFeedPostSchema } from 'schemas/post';
import channelSchema from 'schemas/channel';
import commentSchema from 'schemas/comment';
import feedbackCommentSchema from 'schemas/feedbackComment';
import likeSchema from 'schemas/like';
import activityCategorySchema from 'schemas/activityCategory';
import groupSchema from 'schemas/group';
import challengeSchema from 'schemas/challenge';
import memberSchema from 'schemas/member';
import eventSchema from 'schemas/event';
import feedbackSchema from 'schemas/feedback';
import feedbackTypeSchema from 'schemas/feedbackType';
import recordSchema from 'schemas/record';
import roleSchema from 'schemas/role';
import searchSchema from 'schemas/search';
import translationSchema from 'schemas/translation';
import articleSchema from 'schemas/article';
import contactDirectorySchema from 'schemas/contactDirectory';
import fileGroupSchema from 'schemas/fileGroup';
import notificationSchema from 'schemas/notification';
import pollSchema from 'schemas/poll';
import quizSchema from 'schemas/quiz';
import courseSchema from 'schemas/course';
import learningCourseSchema from 'schemas/learningCourse';
import trainingPlanCourseSchema from 'schemas/trainingPlanCourse';
import trainingPlanLabelSchema from 'schemas/trainingPlanLabel';
import learningLessonSchema from 'schemas/learningLesson';
import learningQuestionSchema from 'schemas/learningQuestion';
import documentFolderSchema from 'schemas/documentFolder';
import documentSectionSchema from 'schemas/documentSection';
import labelSchema from 'schemas/label';
import pageSchema from 'schemas/page';
import collectionSchema from 'schemas/collection';
import collectionAttributeSchema from 'schemas/collectionAttribute';
import collectionCategorySchema from 'schemas/collectionCategory';
import collectionEntrySchema from 'schemas/collectionEntry';
import learningCourseVariantSchema from 'schemas/learningCourseVariant';
import coopIntegrationSchema from 'schemas/coopIntegration';
import sharepointSettings from 'schemas/sharepointSettings';
import sharepointRoot from 'schemas/sharepointRoot';
import integrationRuleSchema from 'schemas/integrationRule';
import integrationSchema from 'schemas/integration';
import { snackbarActions } from 'redux/snackbar';
import { call, put, select } from 'redux-saga/effects';
import { authActions, authSelectors } from 'redux/auth';
import contactSchema from 'schemas/contact';
import conversationSchema from 'schemas/conversation';
import pulseSurveySchema from 'schemas/pulseSurvey';
import pulseScaleSchema from 'schemas/pulseScale';
import pulseSurveyOverviewAnalytics from 'schemas/pulseSurveyOverviewAnalytics';
import pulseSurveyResponseSchema from 'schemas/pulseSurveyResponse';
import pulseSurveyPeriodSchema from 'schemas/pulseSurveyPeriod';
import pulseSurveyScheduleSchema from 'schemas/pulseSurveySchedule';
import pulseSurveyDetailedAnalytics from 'schemas/pulseSurveyDetailedAnalytics';
import pageCategorySchema from 'schemas/pageCategory';
import challengeTemplateSchema from 'schemas/challengeTemplate';
import entrantSchema from 'schemas/entrant';
import learningAttemptSchema from 'schemas/learningAttempt';
import request from './request';

export const api = ({

  auth: {
    // Authentication flow
    login: data => request('/auth/login', 'POST', data, { auth: false }),
    logout: data => request('/auth/logout', 'POST', data, { auth: false }),

    // Password reset flow
    forgot: data => request('/auth/password/forgot', 'POST', data, { auth: false }),
    reset: data => request('/auth/password/reset', 'POST', data, { auth: false }),

    getAuthUser: data => request('/auth/user', 'GET', data, { auth: true }),

    // Registration flow
    verifyToken: data => request('/invitation/verify', 'POST', data, { auth: false }),
    findInvitation: data => request('/invitation/find', 'POST', data, { auth: false }),
    registerSend: data => request('/auth/register/send', 'POST', data, { auth: false }),
    registerValidate: data => request('/auth/register/validate', 'POST', data, { auth: false }),
    register: data => request('/auth/register', 'POST', data, { auth: false }),
    join: data => request('/auth/join', 'POST', data, { auth: false }),

    // Push notification device tokens
    registerDevice: data => request('/auth/device', 'POST', data, { auth: true }),

    // DELETE
    deleteAccount: data => request('/auth/delete', 'POST', data, { auth: true }),

    // Other
    getZendeskToken: data => request('/auth/zendesk-token', 'POST', data),
  },

  openai: {
    chatCompletions: (data, fetchOptions = {}) => request('/api/openai/chat/completions', 'POST', data, { auth: true, fetchOptions }),
  },

  opengraph: {
    fetch: ({ anonymous, ...data }) => request('/api/opengraph/fetch', 'GET', data, { auth: false, anonymous: anonymous ?? true }),
  },

  verification: {
    send: data => request('/verification/send', 'POST', data, { auth: false }),
    validate: data => request('/verification/validate', 'POST', data, { auth: false }),
  },

  superpowers: {
    me: () => request('/api/superpowers/me', 'GET', {}, { auth: true }),
    findMembers: data => request('/api/superpowers/members', 'GET', data, { auth: true }),
    getMember: ({ memberId }) => request(`/api/superpowers/members/${memberId}`, 'GET', {}, { auth: true }),
    getDownloads: () => request('/api/superpowers/downloads', 'GET', {}, { auth: true }),
    downloadFile: ({ key, ...data }) => request(`/api/superpowers/downloads/${key}`, 'GET', data, { auth: true, blob: true }),
  },

  files: {
    getFiles: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/files`, 'GET', data, { schema: [fileGroupSchema] }),
    saveFiles: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/files`, 'PUT', data, { schema: [fileGroupSchema] }),
  },

  guidance: {
    getArticles: data => request('/guidance/article', 'GET', data, { schema: [articleSchema] }),
    getArticleCategories: () => request('/guidance/article/category', 'GET', null),
    getArticle: ({ articleId }) => request(`/guidance/article/${articleId}`, 'GET', null, { schema: articleSchema }),
    addLike: ({ articleId }) => request(`/guidance/article/${articleId}/like`, 'POST'),
    removeLike: ({ articleId }) => request(`/guidance/article/${articleId}/like`, 'DELETE'),
  },

  birthday: {
    getOverview: ({ workspaceId }) => request(`/workspace/${workspaceId}/birthdays-overview`, 'GET'),
    getBirthdays: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/birthdays`, 'GET', { limit: 50, ...data }),
    getBirthdayEvent: ({ memberId, year }) => request(`/member/${memberId}/birthday/${year}`, 'GET'),

    addComment: ({ eventId, ...data }) => request(`/birthday-event/${eventId}/comment`, 'POST', data, { schema: commentSchema, formData: true }),
    getComments: ({ eventId, ...data }) => request(`/birthday-event/${eventId}/comment`, 'GET', data, { schema: [commentSchema] }),
  },

  boost: {
    get: () => request('/boost', 'GET', null, { schema: [boostSchema] }),
    update: ({ boostId, ...data }) => request(`/boost/${boostId}/complete`, 'PUT', data),
  },

  calendar: {
    downloadIcs: ({ eventId, ...data }) => request(`/event/${eventId}/download-ics`, 'GET', data, { blob: true }),
    getEvent: ({ eventId, ...data }) => request(`/event/${eventId}`, 'GET', data, { schema: eventSchema }),
    getEventFeed: ({ eventId, ...data }) => request(`/event/${eventId}/feed`, 'GET', data, { schema: [getFeedPostSchema('event', eventId)] }),
    getEvents: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/event`, 'GET', data, { schema: [eventSchema] }),
    createEvent: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/event`, 'POST', data, { schema: eventSchema, formData: true }),
    updateEvent: ({ eventId, ...data }) => request(`/event/${eventId}`, 'POST', { ...data, _method: 'PUT' }, { schema: eventSchema, formData: true }),
    deleteEvent: ({ eventId, ...data }) => request(`/event/${eventId}`, 'DELETE', data),
  },

  training: {
    getCourse: ({ courseId, ...data }) => request(`/course/${courseId}`, 'GET', data, { schema: courseSchema }),
    getCourseFeed: ({ courseId, ...data }) => request(`/course/${courseId}/feed`, 'GET', data, { schema: [getFeedPostSchema('course', courseId)] }),
    getCourses: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/course`, 'GET', data, { schema: [courseSchema] }),
    createCourse: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/course`, 'POST', data, { schema: courseSchema, formData: true }),
    updateCourse: ({ courseId, ...data }) => request(`/course/${courseId}`, 'POST', { ...data, _method: 'PUT' }, { schema: courseSchema, formData: true }),
    deleteCourse: ({ courseId, ...data }) => request(`/course/${courseId}`, 'DELETE', data),
  },

  drive: {
    getRecord: ({ recordId, ...data }) => request(`/record/${recordId}`, 'GET', data, { schema: recordSchema }),
    getRecordFeed: ({ recordId, ...data }) => request(`/record/${recordId}/feed`, 'GET', data, { schema: [getFeedPostSchema('record', recordId)] }),
    getRecords: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/record`, 'GET', data, { schema: [recordSchema] }),

    createRecord: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/record`, 'POST', data, { schema: recordSchema, formData: true }),
    updateRecord: ({ recordId, ...data }) => request(`/record/${recordId}`, 'POST', { ...data, _method: 'PUT' }, { schema: recordSchema, formData: true }),
    deleteRecord: ({ recordId, ...data }) => request(`/record/${recordId}`, 'DELETE', data),

    getRecordTypes: () => request('/record-type', 'GET'),
  },

  challenge: {
    getChallenge: ({ challengeId }) => request(`/challenge/${challengeId}`, 'GET', null, { schema: challengeSchema }),
    getChallengeEntrants: ({ challengeId, ...data }) => request(`/challenge/${challengeId}/entrants`, 'GET', data, { schema: [entrantSchema] }),
    getChallengeFeed: ({ challengeId, ...data }) => request(`/challenge/${challengeId}/feed`, 'GET', data, { schema: [getFeedPostSchema('challenge', challengeId)] }),
    getChallenges: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/challenge`, 'GET', data, { schema: [challengeSchema] }),
    getChallengesFeed: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/challenge/feed`, 'GET', data, { schema: [getFeedPostSchema('challenges')] }),
    getActivityCategories: ({ workspaceId }) => request(`/workspace/${workspaceId}/activity/category`, 'GET', null, { schema: [activityCategorySchema] }),
    storeActivity: data => request('/activity', 'POST', data, { formData: true }),
    updateActivity: ({ postId, ...data }) => request(`/activity/${postId}`, 'POST', { ...data, _method: 'PUT' }, { formData: true }),
  },

  channel: {
    get: ({ channelId, ...data }) => request(`/channel/${channelId}`, 'GET', data, { schema: channelSchema }),
    getFeed: ({ channelId, ...data }) => request(`/channel/${channelId}/feed`, 'GET', data, { schema: [getFeedPostSchema('channel', channelId)] }),
    subscribe: ({ channelId, ...data }) => request(`/channel/${channelId}/subscribe`, 'POST', data),
  },

  chat: {
    getToken: (data) => request('/chat/token', 'POST', data),
    getConversableMembers: ({ ...data }) => request('/chat/conversable-members', 'GET', data, { schema: [memberSchema] }), // TODO: Better api url

    getConversation: ({ conversationId, ...data }) => request(`/conversation/${conversationId}`, 'GET', data, { schema: conversationSchema }),
    getConversations: ({ conversationIds, ...data }) => request('/conversation', 'GET', { conversationIds, ...data }, { schema: [conversationSchema] }),
    updateConversation: ({ conversationId, ...data }) => request(`/conversation/${conversationId}`, 'POST', { ...data, _method: 'PUT' }, { schema: conversationSchema, formData: true }), // TODO: Better api url
    createConversation: (data) => request('/conversation', 'POST', data, { schema: conversationSchema }),
    markConversationUnread: ({ conversationId, ...data }) => request(`/conversation/${conversationId}/mark-unread`, 'POST', data),

    getMembersForConversation: ({ conversationId, ...data }) => request(`/conversation/${conversationId}/member`, 'GET', data, { schema: [memberSchema] }),
    conversationMemberDelete: ({ conversationId, memberId, ...data }) => request(`/conversation/${conversationId}/member/${memberId}`, 'DELETE', data),
    conversationMemberAdd: ({ conversationId, ...data }) => request(`/conversation/${conversationId}/member`, 'POST', data),
  },

  comment: {
    get: ({ commentId }) => request(`/comment/${commentId}`, 'GET'),
    delete: ({ commentId }) => request(`/comment/${commentId}`, 'DELETE'),
    update: ({ commentId, ...data }) => request(`/comment/${commentId}`, 'PUT', data),
    getLikes: ({ commentId }) => request(`/comment/${commentId}/like`, 'GET', {}, { schema: [likeSchema] }),
    removeLike: ({ commentId }) => request(`/comment/${commentId}/like`, 'DELETE'),
    addLike: ({ commentId, ...data }) => request(`/comment/${commentId}/like`, 'POST', data),
    getTranslation: ({ commentId, ...data }) => request(`/comment/${commentId}/translations`, 'GET', data, { schema: [translationSchema] }),
  },

  jobAnniversary: {
    getOverview: ({ workspaceId }) => request(`/workspace/${workspaceId}/job-anniversaries-overview`, 'GET'),
    getJobAnniversaries: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/job-anniversaries`, 'GET', { limit: 50, ...data }),
    getJobAnniversaryEvent: ({ memberId, year }) => request(`/member/${memberId}/job-anniversary/${year}`, 'GET'),

    addComment: ({ eventId, ...data }) => request(`/job-anniversary-event/${eventId}/comment`, 'POST', data, { schema: commentSchema, formData: true }),
    getComments: ({ eventId, ...data }) => request(`/job-anniversary-event/${eventId}/comment`, 'GET', data, { schema: [commentSchema] }),
  },

  member: {
    getMemberFeed: ({ memberId, ...data }) => request(`/member/${memberId}/feed`, 'GET', data, { schema: [getFeedPostSchema('member', memberId)] }),
  },

  poll: {
    getPoll: ({ pollId, ...data }) => request(`/poll/${pollId}`, 'GET', data, { schema: pollSchema }),
    addOption: ({ pollId, ...data }) => request(`/poll/${pollId}/option`, 'POST', data),
    removeOption: ({ pollId, optionId }) => request(`/poll/${pollId}/option/${optionId}`, 'DELETE'),

    downloadVotes: ({ pollId, ...data }) => request(`/poll/${pollId}/download`, 'GET', data, { blob: true }),

    getVotes: ({ optionId }) => request(`/option/${optionId}/vote`, 'GET', null, { schema: [memberSchema] }),
    storeVote: ({ optionId }) => request(`/option/${optionId}/vote`, 'POST'),
    deleteVote: ({ optionId }) => request(`/option/${optionId}/vote`, 'DELETE'),
  },

  quiz: {
    getQuiz: ({ quizId, ...data }) => request(`/quiz/${quizId}`, 'GET', data, { schema: quizSchema }),
    downloadAnswers: ({ quizId, ...data }) => request(`/quiz/${quizId}/download`, 'GET', data, { blob: true }),
    storeAnswer: ({ quizId, optionId }) => request(`/quiz/${quizId}/option/${optionId}/answer`, 'POST', null, { schema: quizSchema }),
  },

  post: {
    get: ({ postId }) => request(`/post/${postId}`, 'GET', {}, { schema: postSchema }),
    store: data => request('/post', 'POST', data, { formData: true, schema: postSchema }),
    update: ({ postId, ...data }) => request(`/post/${postId}`, 'POST', { ...data, _method: 'PUT' }, { formData: true, schema: postSchema }),
    patch: ({ postId, ...data }) => request(`/post/${postId}`, 'PATCH', data, { schema: postSchema }),
    delete: ({ postId }) => request(`/post/${postId}`, 'DELETE'),
    copy: ({ postId }) => request(`/post/${postId}/copy`, 'POST', {}, { schema: postSchema }),

    getReachPercentages: ({ postIds, ...data }) => request('/post/reach-percentages', 'GET', { postIds, ...data }),

    searchForAdmin: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/admin/post/search`, 'POST', data),
    getAdminFilterOptions: ({ workspaceId }) => request(`/workspace/${workspaceId}/admin/post/filter-options`, 'GET'),

    shareToNetwork: ({ postId }) => request(`/post/${postId}/network-share`, 'POST'),
    getNetworkFeed: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/admin/network/feed`, 'GET', data),
    resolveNetworkShare: ({ workspaceId, targetId, ...data }) => request(`/workspace/${workspaceId}/admin/network/share-targets/${targetId}`, 'PATCH', data),
    publishNetworkShare: ({ workspaceId, targetId, ...data }) => request(`/workspace/${workspaceId}/admin/network/share-targets/${targetId}/publish`, 'POST', data, { formData: true }),
    withdrawNetworkShare: ({ workspaceId, shareId }) => request(`/workspace/${workspaceId}/admin/network/shares/${shareId}`, 'DELETE'),
    getNetworkSettings: ({ workspaceId }) => request(`/workspace/${workspaceId}/admin/network/settings`, 'GET'),
    getNetworkAutoPostCandidates: ({ workspaceId }) => request(`/workspace/${workspaceId}/admin/network/auto-post-candidates`, 'GET', {}, { schema: [memberSchema] }),
    updateNetworkSettings: ({ workspaceId, networkId, ...data }) => request(`/workspace/${workspaceId}/admin/network/${networkId}/settings`, 'PATCH', data),

    addComment: ({ postId, ...data }) => request(`/post/${postId}/comment`, 'POST', data, { schema: commentSchema, formData: true }),
    getComments: ({ postId, ...data }) => request(`/post/${postId}/comment`, 'GET', data, { schema: [commentSchema] }),

    getLikes: ({ postId }) => request(`/post/${postId}/like`, 'GET', {}, { schema: [likeSchema] }),
    removeLike: ({ postId }) => request(`/post/${postId}/like`, 'DELETE'),
    addLike: ({ postId, ...data }) => request(`/post/${postId}/like`, 'POST', data),

    getSmsExpenseEstimation: data => request('/post/sms-expense-estimation', 'POST', data),

    getTranslations: ({ postId, ...data }) => request(`/post/${postId}/translations`, 'GET', data, { schema: [translationSchema] }),

    follow: ({ postId }) => request(`/post/${postId}/follow`, 'POST', { postId, follow: true }, { schema: postSchema }),
    unfollow: ({ postId }) => request(`/post/${postId}/follow`, 'POST', { postId, follow: false }, { schema: postSchema }),
  },

  group: {
    get: ({ id }) => request(`/group/${id}`, 'GET', {}, { schema: groupSchema }),
    getPosts: ({ groupId, ...data }) => request(`/group/${groupId}/post`, 'GET', data, { schema: [getFeedPostSchema('group', groupId)] }),
    subscribe: ({ id, subscribe }) => request(`/group/${id}/subscribe`, 'POST', { subscribe }),
    store: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/group`, 'POST', data, { schema: groupSchema, formData: true }),
    update: ({ groupId, ...data }) => request(`/group/${groupId}`, 'POST', { ...data, _method: 'PUT' }, { formData: true, schema: groupSchema }),
    delete: ({ groupId }) => request(`/group/${groupId}`, 'DELETE'),
    getMembers: ({ groupId, ...data }) => request(`/group/${groupId}/member`, 'GET', data, { schema: [memberSchema] }),
  },

  contract: {

    get: data => request('/contract', 'GET', data, { auth: false }),
    download: ({ contractId, ...data }) => request(`/contract/${contractId}/download`, 'GET', data, { blob: true }),

    workspace: {
      getContracts: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/contract`, 'GET', data),
      downloadContract: ({ workspaceId, contractId, ...data }) => request(`/workspace/${workspaceId}/contract/${contractId}/download`, 'GET', data, { blob: true }),
      acceptContract: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/contract`, 'POST', data),
    },
  },

  integrations: {
    getAll: ({ workspaceId }) => request(`/workspace/${workspaceId}/integrations`, 'GET'),
    update: ({ workspaceId, key, ...data }) => request(`/workspace/${workspaceId}/integrations/${key}`, 'PATCH', data),
    sync: ({ workspaceId, key }) => request(`/workspace/${workspaceId}/integrations/${key}/sync`, 'POST'),
  },

  integration: {
    index: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/integration`, 'GET', data, { schema: [integrationSchema] }),
    patch: ({ workspaceId, integrationId, ...data }) => request(`/workspace/${workspaceId}/integration/${integrationId}`, 'PATCH', data, { schema: integrationSchema }),

    rules: {
      search: ({ workspaceId, integrationId, ...data }) => request(`/workspace/${workspaceId}/integration/${integrationId}/rule/search`, 'POST', data),
      export: ({ workspaceId, integrationId, ...data }) => request(`/workspace/${workspaceId}/integration/${integrationId}/rule`, 'GET', data, { blob: true, headers: { Accept: 'text/csv' } }),

      create: ({ workspaceId, integrationId, ...data }) => request(`/workspace/${workspaceId}/integration/${integrationId}/rule`, 'POST', data, { schema: integrationRuleSchema }),
      update: ({ integrationRuleId, integrationId, workspaceId, ...data }) => request(`/workspace/${workspaceId}/integration/${integrationId}/rule/${integrationRuleId}`, 'PUT', data, { schema: integrationRuleSchema }),

      massaction: ({ integrationId, workspaceId, ...data }) => request(`/workspace/${workspaceId}/integration/${integrationId}/rule/massaction`, 'POST', data, { schema: [integrationRuleSchema] }),
      autocomplete: ({ integrationId, workspaceId, ...data }) => request(`/workspace/${workspaceId}/integration/${integrationId}/rule/autocomplete`, 'GET', data),
    },
    persona: {
      get: ({ workspaceId }) => request(`/workspace/${workspaceId}/integration/persona/settings`, 'GET'),
      update: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/integration/persona/settings`, 'PUT', data),
    },
    netvisor: {
      get: ({ workspaceId }) => request(`/workspace/${workspaceId}/integration/netvisor/settings`, 'GET'),
      update: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/integration/netvisor/settings`, 'PUT', data),
      sync: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/integration/netvisor/sync`, 'POST', data),
    },
    coop: {
      get: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/integration/coop/settings`, 'GET', data, { schema: coopIntegrationSchema }),
      update: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/integration/coop/settings`, 'PUT', data),
      sync: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/integration/coop/sync`, 'POST', data),
    },
  },

  sharepoint: {
    get: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/sharepoint/settings`, 'GET', data, { schema: sharepointSettings }),

    syncRoot: ({ workspaceId, sharepointRootId, ...data }) => request(`/workspace/${workspaceId}/sharepoint-root/${sharepointRootId}/sync`, 'POST', data, { schema: sharepointRoot }),

    getAdminConsentUrl: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/sharepoint/get-admin-consent-url`, 'POST', data),
    getSiteConsentUrl: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/sharepoint/get-site-consent-url`, 'POST', data),
  },

  feedback: {
    store: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/feedback`, 'POST', data, { formData: true, schema: feedbackSchema }),
    publicStore: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/feedback`, 'POST', data, { formData: true, anonymous: true, auth: false, schema: undefined }),
    patch: ({ feedbackId, ...data }) => request(`/feedback/${feedbackId}`, 'POST', { ...data, _method: 'PATCH' }, { formData: true, schema: feedbackSchema }),
    massAction: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/feedback/massaction`, 'POST', data),
    index: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/feedback`, 'GET', data, { schema: [feedbackSchema] }),
    search: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/feedback/search`, 'POST', data),
    show: ({ feedbackId, ...data }) => request(`/feedback/${feedbackId}`, 'GET', data, { schema: feedbackSchema, anonymous: !!data.token, auth: !data.token }),
    delete: ({ feedbackId, ...data }) => request(`/feedback/${feedbackId}`, 'DELETE', data),

    download: ({ feedbackId, ...data }) => request(`/feedback/${feedbackId}/download`, 'GET', data, { blob: true }),
    downloadIndex: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/feedback/download`, 'GET', data, { blob: true }),
    export: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/feedback/export`, 'POST', data, { blob: true, headers: { 'Content-Type': 'application/json' } }),
  },

  feedbackComments: {
    index: ({ feedbackId, ...data }) => request(`/feedback/${feedbackId}/comment`, 'GET', data, { schema: [feedbackCommentSchema], anonymous: !!data.token, auth: !data.token }),
    store: ({ feedbackId, ...data }) => request(`/feedback/${feedbackId}/comment`, 'POST', data, { schema: feedbackCommentSchema, formData: true, anonymous: !!data.token, auth: !data.token }),
    show: ({ feedbackId, feedbackCommentId, ...data }) => request(`/feedback/${feedbackId}/comment/${feedbackCommentId}`, 'GET', data),
    update: ({ feedbackId, feedbackCommentId, ...data }) => request(`/feedback/${feedbackId}/comment/${feedbackCommentId}`, 'PUT', data, { schema: feedbackCommentSchema }),
    delete: ({ feedbackId, feedbackCommentId, ...data }) => request(`/feedback/${feedbackId}/comment/${feedbackCommentId}`, 'DELETE', data),
    translations: ({ feedbackId, feedbackCommentId, ...data }) => request(`/feedback/${feedbackId}/comment/${feedbackCommentId}/translations`, 'GET', data, { schema: [translationSchema], anonymous: !!data.token, auth: !data.token }),
  },

  feedbackStatus: {
    index: (data) => request('/feedback-status', 'GET', data),
  },

  feedbackSettings: {
    get: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/feedback-settings`, 'GET', data),
    update: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/feedback-settings`, 'PUT', data),
  },

  feedbackType: {
    index: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/feedback-type`, 'GET', data, { schema: [feedbackTypeSchema] }),
    store: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/feedback-type`, 'POST', data, { schema: feedbackTypeSchema }),
    show: ({ feedbackTypeId, ...data }) => request(`/feedback-type/${feedbackTypeId}`, 'GET', data, { schema: feedbackTypeSchema, auth: !data.token, anonymous: !!data.token }),
    update: ({ feedbackTypeId, ...data }) => request(`/feedback-type/${feedbackTypeId}`, 'PUT', data, { schema: feedbackTypeSchema }),
    patch: ({ feedbackTypeId, ...data }) => request(`/feedback-type/${feedbackTypeId}`, 'PATCH', data, { schema: feedbackTypeSchema }),
    delete: ({ feedbackTypeId, ...data }) => request(`/feedback-type/${feedbackTypeId}`, 'DELETE', data),

    getPublicUrl: ({ feedbackTypeId, ...data }) => request(`/feedback-type/${feedbackTypeId}/public-url`, 'GET', data),
    getAccessRemovalSummary: ({ feedbackTypeId, ...data }) => request(`/feedback-type/${feedbackTypeId}/access-removal-summary`, 'GET', data),
    getFilterFields: ({ feedbackTypeId, ...data }) => request(`/feedback-type/${feedbackTypeId}/filter-fields`, 'GET', data),
    getFilterFieldSuggestions: ({ feedbackTypeId, ...data }) => request(`/feedback-type/${feedbackTypeId}/filter-field-suggestions`, 'GET', data),
  },

  feedbackQuestion: {
    autocomplete: ({ feedbackQuestionId, ...data }) => request(`/feedback-question/${feedbackQuestionId}/autocomplete`, 'GET', data, { auth: !data.token, anonymous: !!data.token }),
  },

  translation: {
    search: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/translation/search`, 'POST', data),
    batchUpdate: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/translation/batch`, 'PUT', data),
    autoTranslate: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/translation/auto-translate`, 'POST', data),
  },

  workspace: {
    get: ({ workspaceId }) => request(`/workspace/${workspaceId}`, 'GET'),
    home: ({ workspaceId }) => request(`/workspace/${workspaceId}/home`, 'GET'),
    respondToNotice: ({ workspaceId, uuid, action }) => request(`/workspace/${workspaceId}/notices/${uuid}/responses`, 'POST', { action }),
    channels: ({ workspaceId }) => request(`/workspace/${workspaceId}/channel`, 'GET', null, { schema: [channelSchema] }),
    homeFeed: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/feed`, 'GET', data, { schema: [getFeedPostSchema('home')] }),
    groups: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/group`, 'GET', data, { schema: [groupSchema] }),
    groupsFeed: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/group/feed`, 'GET', data, { schema: [getFeedPostSchema('groups')] }),

    label: ({ workspaceId, labelId, ...data }) => request(`/workspace/${workspaceId}/labels/${labelId}`, 'GET', data, { schema: labelSchema }),
    labels: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/labels`, 'GET', data, { schema: [labelSchema] }),
    storeLabel: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/labels`, 'POST', data, { schema: [labelSchema] }),
    updateLabel: ({ workspaceId, labelId, ...data }) => request(`/workspace/${workspaceId}/labels/${labelId}`, 'PUT', data, { schema: labelSchema }),
    deleteLabel: ({ workspaceId, labelId, ...data }) => request(`/workspace/${workspaceId}/labels/${labelId}`, 'DELETE', data),

    labelMembers: ({ workspaceId, labelId, ...data }) => request(`/workspace/${workspaceId}/label/${labelId}/members`, 'GET', data, { schema: [memberSchema] }),
    searchLabelMembers: ({ workspaceId, labelId, ...data }) => request(`/workspace/${workspaceId}/label/${labelId}/search-members`, 'GET', data, { schema: [memberSchema] }),
    addLabelToMember: ({ workspaceId, labelId, memberId, ...data }) => request(`/workspace/${workspaceId}/label/${labelId}/member/${memberId}`, 'POST', data),
    removeLabelFromMember: ({ workspaceId, labelId, memberId, ...data }) => request(`/workspace/${workspaceId}/label/${labelId}/member/${memberId}`, 'DELETE', data),

    updateLabelToCourseRelation: ({ workspaceId, labelId, courseId, ...data }) => request(`/workspace/${workspaceId}/label/${labelId}/course/${courseId}`, 'PUT', data),

    invitations: ({ workspaceId }) => request(`/workspace/${workspaceId}/invitation`, 'GET'),
    createInvitation: ({ workspaceId }) => request(`/workspace/${workspaceId}/invitation`, 'POST'),
    updateInvitation: ({ workspaceId, invitationId, ...data }) => request(`/workspace/${workspaceId}/invitation/${invitationId}`, 'PATCH', data),
    invalidateInvitation: ({ workspaceId, invitationId }) => request(`/workspace/${workspaceId}/invitation/${invitationId}`, 'DELETE'),
    regenerateInvitation: ({ workspaceId, invitationId }) => request(`/workspace/${workspaceId}/invitation/${invitationId}/regenerate`, 'POST'),

    settings: ({ workspaceId }) => request(`/workspace/${workspaceId}/settings`, 'GET'),
    updateSettings: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/settings`, 'POST', { ...data, _method: 'PUT' }, { formData: true }),

    profile: ({ workspaceId }) => request(`/workspace/${workspaceId}/profile`, 'GET'),
    updateProfile: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/profile`, 'POST', { ...data, _method: 'PUT' }, { formData: true }),

    getUser: ({ workspaceId }) => request(`/workspace/${workspaceId}/user`, 'GET'),
    getMe: ({ workspaceId }) => request(`/workspace/${workspaceId}/me`, 'GET'),
    updateUser: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/user`, 'POST', { ...data, _method: 'PUT' }),

    getContactDirectory: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/people-directory`, 'GET', data, { schema: [contactDirectorySchema] }),

    search: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/search`, 'GET', data, { schema: [searchSchema] }),

    contact: {
      store: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/contact`, 'POST', data, { schema: contactSchema }),
      show: ({ workspaceId, contactId, ...data }) => request(`/workspace/${workspaceId}/contact/${contactId}`, 'GET', data, { schema: contactSchema }),
      update: ({ workspaceId, contactId, ...data }) => request(`/workspace/${workspaceId}/contact/${contactId}`, 'PUT', data, { schema: contactSchema }),
      delete: ({ workspaceId, contactId, ...data }) => request(`/workspace/${workspaceId}/contact/${contactId}`, 'DELETE', data),
    },

    wysiwyg: {
      storeImage: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/wysiwyg/image`, 'POST', data, { formData: true }),
      storeFile: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/wysiwyg/file`, 'POST', data, { formData: true }),
      processVideo: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/wysiwyg/process-video`, 'POST', data),
    },

    roles: {
      index: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/roles`, 'GET', data, { schema: [roleSchema] }),
    },

    getStructuralUnits: ({ workspaceId }) => request(`/workspace/${workspaceId}/structural-units`, 'GET'),

    challenge: {
      get: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/challenge`, 'GET', data, { schema: [challengeSchema] }),
      templates: ({ workspaceId }) => request(`/workspace/${workspaceId}/challenge/template`, 'GET', {}, { schema: [challengeTemplateSchema] }),
      store: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/challenge`, 'POST', data),
      update: ({ challengeId, ...data }) => request(`/challenge/${challengeId}`, 'POST', { _method: 'PUT', ...data }),
      download: ({ challengeId, ...data }) => request(`/challenge/${challengeId}/export`, 'GET', data, { blob: true }),
    },

    analytics: {
      getOverview: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/analytics/overview`, 'GET', data),
      getFunnel: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/analytics/funnel`, 'GET', data),
      getActiveness: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/analytics/activeness`, 'GET', data),
      getAdoption: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/analytics/adoption`, 'GET', data),
      getEngagement: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/analytics/engagement`, 'GET', data),
      getPosts: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/analytics/posts`, 'GET', data),
      getLikes: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/analytics/likes`, 'GET', data),
      getMembers: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/analytics/members`, 'GET', data),
      getUsers: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/analytics/users`, 'GET', data),
      getComments: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/analytics/comments`, 'GET', data),
    },

    member: {
      index: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/member`, 'GET', data, { schema: [memberSchema] }),
      show: ({ workspaceId, memberId, ...data }) => request(`/workspace/${workspaceId}/member/${memberId}`, 'GET', data, { schema: memberSchema }),
      export: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/member`, 'GET', data, { schema: [memberSchema], blob: true, headers: { Accept: 'text/csv' } }),
      store: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/member`, 'POST', data, { schema: memberSchema }),
      delete: ({ workspaceId, memberId }) => request(`/workspace/${workspaceId}/member/${memberId}`, 'DELETE'),
      update: ({ workspaceId, memberId, ...data }) => request(`/workspace/${workspaceId}/member/${memberId}`, 'PUT', data, { schema: memberSchema }),
      reinvite: ({ workspaceId, memberId }) => request(`/workspace/${workspaceId}/member/${memberId}/invite`, 'POST'),
      massAction: ({ workspaceId, members, action, ...data }) => request(`/workspace/${workspaceId}/member/massaction`, 'POST', { action, members, ...data }),

      patch: ({ workspaceId, memberId, ...data }) => request(`/workspace/${workspaceId}/member/${memberId}`, 'POST', { ...data, _method: 'PATCH' }, { formData: true }),
      removeAvatar: ({ workspaceId, memberId }) => request(`/workspace/${workspaceId}/member/${memberId}/avatar`, 'DELETE', null),
    },
  },

  collections: {
    index: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/collection`, 'GET', data, { schema: [collectionSchema] }),
    show: ({ workspaceId, collectionId, ...data }) => request(`/workspace/${workspaceId}/collection/${collectionId}`, 'GET', data, { schema: collectionSchema }),
    store: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/collection`, 'POST', data, { schema: collectionSchema }),
    update: ({ workspaceId, collectionId, ...data }) => request(`/workspace/${workspaceId}/collection/${collectionId}`, 'PUT', data, { schema: collectionSchema }),
    delete: ({ workspaceId, collectionId, ...data }) => request(`/workspace/${workspaceId}/collection/${collectionId}`, 'DELETE', data),

    categories: {
      store: ({ workspaceId, collectionId, ...data }) => request(`/workspace/${workspaceId}/collection/${collectionId}/category`, 'POST', data, { schema: collectionCategorySchema, formData: true }),
      update: ({ workspaceId, collectionId, categoryId, ...data }) => request(`/workspace/${workspaceId}/collection/${collectionId}/category/${categoryId}`, 'POST', { ...data, _method: 'PUT' }, { schema: collectionCategorySchema, formData: true }),
      delete: ({ workspaceId, collectionId, categoryId, ...data }) => request(`/workspace/${workspaceId}/collection/${collectionId}/category/${categoryId}`, 'DELETE', data),
    },

    attributes: {
      store: ({ workspaceId, collectionId, ...data }) => request(`/workspace/${workspaceId}/collection/${collectionId}/attribute`, 'POST', data, { schema: collectionAttributeSchema }),
      update: ({ workspaceId, collectionId, attributeId, ...data }) => request(`/workspace/${workspaceId}/collection/${collectionId}/attribute/${attributeId}`, 'PUT', data, { schema: collectionAttributeSchema }),
      delete: ({ workspaceId, collectionId, attributeId, ...data }) => request(`/workspace/${workspaceId}/collection/${collectionId}/attribute/${attributeId}`, 'DELETE', data),
    },

    entries: {
      index: ({ workspaceId, collectionId, ...data }) => request(`/workspace/${workspaceId}/collection/${collectionId}/entry`, 'GET', data, { schema: [collectionEntrySchema] }),
      export: ({ workspaceId, collectionId, ...data }) => request(`/workspace/${workspaceId}/collection/${collectionId}/entry/export`, 'GET', data, { blob: true }),
      store: ({ workspaceId, collectionId, ...data }) => request(`/workspace/${workspaceId}/collection/${collectionId}/entry`, 'POST', data, { schema: collectionEntrySchema, formData: true }),
      update: ({ workspaceId, collectionId, entryId, ...data }) => request(`/workspace/${workspaceId}/collection/${collectionId}/entry/${entryId}`, 'POST', { ...data, _method: 'PUT' }, { schema: collectionEntrySchema, formData: true }),
      delete: ({ workspaceId, collectionId, entryId, ...data }) => request(`/workspace/${workspaceId}/collection/${collectionId}/entry/${entryId}`, 'DELETE', data),
    },
  },

  pages: {
    search: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/page/search`, 'POST', data),

    index: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/page`, 'GET', data, { schema: [pageSchema] }),
    store: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/page`, 'POST', data, { schema: pageSchema, formData: true }),
    patch: ({ workspaceId, pageId, ...data }) => request(`/workspace/${workspaceId}/page/${pageId}`, 'PATCH', data, { schema: pageSchema }),
    show: ({ workspaceId, pageId, ...data }) => request(`/workspace/${workspaceId}/page/${pageId}`, 'GET', data, { schema: pageSchema }),
    update: ({ workspaceId, pageId, ...data }) => request(`/workspace/${workspaceId}/page/${pageId}`, 'POST', { ...data, _method: 'PUT' }, { schema: pageSchema, formData: true }),
    delete: ({ workspaceId, pageId, ...data }) => request(`/workspace/${workspaceId}/page/${pageId}`, 'DELETE', data),
    collections: ({ workspaceId, pageId, ...data }) => request(`/workspace/${workspaceId}/page/${pageId}/collections`, 'GET', data),

    categories: {
      index: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/page-category`, 'GET', data, { schema: [pageCategorySchema] }),
      create: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/page-category`, 'POST', data, { schema: pageCategorySchema }),
      update: ({ workspaceId, categoryId, ...data }) => request(`/workspace/${workspaceId}/page-category/${categoryId}`, 'PUT', data, { schema: pageCategorySchema }),
      delete: ({ workspaceId, categoryId, ...data }) => request(`/workspace/${workspaceId}/page-category/${categoryId}`, 'DELETE', data),
    },

    getKnowledgeHub: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/knowledge-hub`, 'GET', data, { schema: { categories: [pageCategorySchema], rootPages: [pageSchema] } }),
  },


  pulse: {
    getPulseSurveys: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/pulse-survey`, 'GET', data, { schema: [pulseSurveySchema] }),
    storePulseSurvey: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/pulse-survey`, 'POST', data, { schema: pulseSurveySchema, formData: true }),
    showPulseSurvey: ({ workspaceId, pulseSurveyId, ...data }) => request(`/workspace/${workspaceId}/pulse-survey/${pulseSurveyId}`, 'GET', data, { schema: pulseSurveySchema }),
    showActivePulseSurvey: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/pulse-survey/active`, 'GET', data, { schema: pulseSurveySchema }),
    updatePulseSurvey: ({ workspaceId, pulseSurveyId, ...data }) => request(`/workspace/${workspaceId}/pulse-survey/${pulseSurveyId}`, 'POST', { ...data, _method: 'PUT' }, { schema: pulseSurveySchema, formData: true }),
    patchPulseSurvey: ({ workspaceId, pulseSurveyId, ...data }) => request(`/workspace/${workspaceId}/pulse-survey/${pulseSurveyId}`, 'PATCH', data, { schema: pulseSurveySchema }),
    deletePulseSurvey: ({ workspaceId, pulseSurveyId, ...data }) => request(`/workspace/${workspaceId}/pulse-survey/${pulseSurveyId}`, 'DELETE', data),
    getDepartments: ({ workspaceId, pulseSurveyId, ...data }) => request(`/workspace/${workspaceId}/pulse-survey/${pulseSurveyId}/departments`, 'GET', data),
    getLocations: ({ workspaceId, pulseSurveyId, ...data }) => request(`/workspace/${workspaceId}/pulse-survey/${pulseSurveyId}/locations`, 'GET', data),
    showPulseSurveyOverviewAnalytics: ({ workspaceId, pulseSurveyId, ...data }) => request(`/workspace/${workspaceId}/pulse-survey/${pulseSurveyId}/overview-analytics`, 'GET', data, { schema: pulseSurveyOverviewAnalytics }),
    showPulseSurveyDetailedAnalytics: ({ workspaceId, pulseSurveyId, ...data }) => request(`/workspace/${workspaceId}/pulse-survey/${pulseSurveyId}/detailed-analytics`, 'GET', data, { schema: [pulseSurveyDetailedAnalytics] }),
    getPulseScales: ({ workspaceId, ...data }) => request(`/workspace/${workspaceId}/pulse-scale`, 'GET', data, { schema: [pulseScaleSchema] }),
    getPulseSurveyResponses: ({ workspaceId, pulseSurveyId, ...data }) => request(`/workspace/${workspaceId}/pulse-survey/${pulseSurveyId}/response`, 'GET', data, { schema: [pulseSurveyResponseSchema] }),
    storePulseSurveyResponse: ({ workspaceId, pulseSurveyId, ...data }) => request(`/workspace/${workspaceId}/pulse-survey/${pulseSurveyId}/response`, 'POST', data),
    downloadPulseSurveyResponses: ({ workspaceId, pulseSurveyId, ...data }) => request(`/workspace/${workspaceId}/pulse-survey/${pulseSurveyId}/response/download`, 'GET', data, { blob: true }),
    getPulseSurveyPeriods: ({ workspaceId, pulseSurveyId, ...data }) => request(`/workspace/${workspaceId}/pulse-survey/${pulseSurveyId}/period`, 'GET', data, { schema: [pulseSurveyPeriodSchema] }),
    getPulseSurveySchedules: ({ workspaceId, pulseSurveyId, ...data }) => request(`/workspace/${workspaceId}/pulse-survey/${pulseSurveyId}/schedule`, 'GET', data, { schema: [pulseSurveyScheduleSchema] }),
    storePulseSurveySchedule: ({ workspaceId, pulseSurveyId, ...data }) => request(`/workspace/${workspaceId}/pulse-survey/${pulseSurveyId}/schedule`, 'POST', data, { schema: pulseSurveyScheduleSchema, formData: true }),
    updatePulseSurveySchedule: ({ workspaceId, pulseSurveyScheduleId, ...data }) => request(`/workspace/${workspaceId}/pulse-survey/schedule/${pulseSurveyScheduleId}`, 'POST', { ...data, _method: 'PUT' }, { schema: pulseSurveyScheduleSchema, formData: true }),
    deletePulseSurveySchedule: ({ workspaceId, pulseSurveyScheduleId, ...data }) => request(`/workspace/${workspaceId}/pulse-survey/schedule/${pulseSurveyScheduleId}`, 'DELETE', data),
  },

  user: {
    sendAnalytics: data => request('/user/action', 'POST', data),

    getProfile: () => request('/user/profile', 'GET'),
    getSettings: () => request('/user/settings', 'GET'),
    updateProfile: data => request('/user/profile', 'POST', { ...data, _method: 'PUT' }),
    updateSettings: data => request('/user/settings', 'POST', { ...data, _method: 'PUT' }),
    getNotificationSettings: () => request('/user/notification/settings', 'GET'),
    updateNotificationSettings: data => request('/user/notification/settings', 'PUT', data),
    updateAvatar: data => request('/user/profile/avatar', 'POST', { ...data, _method: 'PUT' }, { formData: true }),
    deleteAvatar: () => request('/user/profile/avatar', 'DELETE'),
    getNotifications: data => request('/user/notification', 'GET', data, { schema: [notificationSchema] }),
    readNotifications: data => request('/user/notification/read', 'POST', data),
    getBadges: () => request('/user/badges', 'GET'),

    sendEmailChangeVerification: data => request('/user/email/', 'POST', data),
  },

  learning: {
    trainingPlan: {
      byCourses: ({ workspaceId, ...data }) => request(`/learning/workspace/${workspaceId}/training-plan/courses`, 'GET', data, { schema: [trainingPlanCourseSchema] }),
      byLabels: ({ workspaceId, ...data }) => request(`/learning/workspace/${workspaceId}/training-plan/labels`, 'GET', data, { schema: [trainingPlanLabelSchema] }),
    },

    getCourses: data => request('/learning/course/', 'GET', data, { schema: [learningCourseSchema] }),
    getCourse: ({ courseId, ...data }) => request(`/learning/course/${courseId}`, 'GET', data, { schema: learningCourseSchema }),
    changeCourseVariantPreference: ({ courseId, ...data }) => request(`/learning/course/${courseId}/change-variant-preference`, 'PUT', data, { schema: learningCourseSchema }),
    getLessons: ({ courseVariantId, ...data }) => request(`/learning/course-variant/${courseVariantId}/lesson`, 'GET', data, { schema: [learningLessonSchema] }),
    getLesson: ({ lessonId, ...data }) => request(`/learning/lesson/${lessonId}`, 'GET', data, { schema: learningLessonSchema }),
    getQuestions: ({ lessonId, ...data }) => request(`/learning/lesson/${lessonId}/question`, 'GET', data, { schema: [learningQuestionSchema] }),
    getQuestion: ({ questionId, ...data }) => request(`/learning/question/${questionId}`, 'GET', data, { schema: learningQuestionSchema }),
    postQuestionAnswer: ({ questionId, ...data }) => request(`/learning/question/${questionId}/answer`, 'POST', data),

    getCoursesForAdmin: data => request('/admin/learning/course/', 'GET', data, { schema: [learningCourseSchema] }),
    searchCoursesForAdmin: data => request('/admin/learning/course/search', 'POST', data),
    getCourseForAdmin: ({ courseId, ...data }) => request(`/admin/learning/course/${courseId}`, 'GET', data, { schema: learningCourseSchema }),
    postCourseForAdmin: data => request('/admin/learning/course/', 'POST', data, { schema: learningCourseSchema }),
    putCourseForAdmin: ({ courseId, ...data }) => request(`/admin/learning/course/${courseId}`, 'PUT', data, { schema: learningCourseSchema }),
    deleteCourseVariantForAdmin: ({ courseVariantId, ...data }) => request(`/admin/learning/course-variant/${courseVariantId}`, 'DELETE', data),
    duplicateCourseVariantForAdmin: ({ courseVariantId, ...data }) => request(`/admin/learning/course-variant/${courseVariantId}/duplicate`, 'POST', data, { schema: learningCourseVariantSchema }),
    archiveCourseVariantForAdmin: ({ courseVariantId, ...data }) => request(`/admin/learning/course-variant/${courseVariantId}/archive`, 'POST', { ...data, _method: 'PUT' }, { schema: learningCourseVariantSchema }),
    expireCourseAttemptForAdmin: ({ courseVariantId, ...data }) => request(`/admin/learning/course-variant/${courseVariantId}/expire-attempt`, 'POST', { ...data, _method: 'PUT' }),
    downloadCourseVariantFreeformAnswersForAdmin: ({ courseVariantId, ...data }) => request(`/admin/learning/course-variant/${courseVariantId}/download-freeform-answers`, 'GET', { ...data }, { blob: true }),
    getLessonsForAdmin: ({ courseVariantId, ...data }) => request(`/admin/learning/course-variant/${courseVariantId}/lesson`, 'GET', data, { schema: [learningLessonSchema] }),
    getLessonForAdmin: ({ lessonId, ...data }) => request(`/admin/learning/lesson/${lessonId}`, 'GET', data, { schema: learningLessonSchema }),
    postLessonImageForAdmin: ({ courseVariantId, formData }) => request(`/admin/learning/course-variant/${courseVariantId}/lesson/image`, 'POST', formData, { formData: true }),
    postLessonForAdmin: ({ courseVariantId, ...data }) => request(`/admin/learning/course-variant/${courseVariantId}/lesson`, 'POST', data, { formData: true, schema: learningLessonSchema }),
    putLessonForAdmin: ({ lessonId, ...data }) => request(`/admin/learning/lesson/${lessonId}`, 'POST', { ...data, _method: 'PUT' }, { formData: true }),
    deleteLessonForAdmin: ({ lessonId, ...data }) => request(`/admin/learning/lesson/${lessonId}`, 'DELETE', data),

    courseMembersMassAction: ({ courseId, ...data }) => request(`/admin/learning/course/${courseId}/members/massaction`, 'POST', data, { formData: true }),

    reorderLessonForAdmin: ({ courseVariantId, ...data }) => request(`/admin/learning/course-variant/${courseVariantId}/reorder`, 'PUT', data),

    getCoursesAnalytics: data => request('/admin/learning/analytics/courses/', 'GET', data),
    getCourseMembersAnalytics: ({ courseId, ...data }) => request(`/admin/learning/analytics/courses/${courseId}/members`, 'GET', data),
    getCourseMembersAnalyticsDownload: ({ courseId, ...data }) => request(`/admin/learning/analytics/courses/${courseId}/members/download`, 'GET', data, { blob: true }),
    getCourseLessonsAnalytics: ({ courseId, ...data }) => request(`/admin/learning/analytics/courses/${courseId}/lessons`, 'GET', data),
    getCourseLessonsAnalyticsDownload: ({ courseId, ...data }) => request(`/admin/learning/analytics/courses/${courseId}/lessons/download`, 'GET', data, { blob: true }),
    getCourseVariantQuestionsAnalytics: ({ courseVariantId, ...data }) => request(`/admin/learning/analytics/course-variant/${courseVariantId}/questions`, 'GET', data),
    getLessonQuestionsAnalytics: ({ lessonId, ...data }) => request(`/admin/learning/analytics/lessons/${lessonId}/questions`, 'GET', data),
    getMembersAnalytics: data => request('/admin/learning/analytics/members/', 'GET', data),
    getMemberCoursesAnalytics: ({ memberId, ...data }) => request(`/admin/learning/analytics/members/${memberId}/courses`, 'GET', data),
    getMemberCoursesAnalyticsDownload: ({ memberId, ...data }) => request(`/admin/learning/analytics/members/${memberId}/courses/download`, 'GET', data, { blob: true }),

    getMemberCourseVariantAnalytics: ({ memberId, courseVariantId, ...data }) => request(`/admin/learning/analytics/members/${memberId}/course-variant/${courseVariantId}`, 'GET', data),
    getMemberCourseVariantsAnalytics: ({ memberId, courseId, ...data }) => request(`/admin/learning/analytics/members/${memberId}/courses/${courseId}/variants`, 'GET', data),

    getMemberCourseAttemptsAnalytics: ({ memberId, courseId, ...data }) => request(`/admin/learning/analytics/members/${memberId}/courses/${courseId}/attempts`, 'GET', data, { schema: [learningAttemptSchema] }),
    getMemberCourseAttemptAnalytics: ({ attemptId, ...data }) => request(`/admin/learning/analytics/attempt/${attemptId}`, 'GET', data),

    getMemberCourseAnalytics: ({ memberId, courseId, ...data }) => request(`/admin/learning/analytics/members/${memberId}/courses/${courseId}`, 'GET', data),
    getMemberCourseAnalyticsCsv: ({ memberId, courseId, ...data }) => request(`/admin/learning/analytics/members/${memberId}/courses/${courseId}/csv`, 'GET', data, { blob: true }),
    getMemberCourseAnalyticsPdf: ({ memberId, courseId, ...data }) => request(`/admin/learning/analytics/members/${memberId}/courses/${courseId}/pdf`, 'GET', data, { blob: true }),
    getLabelsAnalytics: data => request('/admin/learning/analytics/labels/', 'GET', data),
    getLabelCoursesAnalytics: ({ labelId, ...data }) => request(`/admin/learning/analytics/labels/${labelId}/courses`, 'GET', data),

    getCourseInternalTypeSuggestions: (data) => request('/admin/learning/course-internal-type/suggestions', 'GET', data),
  },

  documents: {
    getFolders: data => request('/document/folder/', 'GET', data, { schema: [documentFolderSchema] }),
    getFolder: ({ folderId, ...data }) => request(`/document/folder/${folderId}`, 'GET', data, { schema: documentFolderSchema }),
    getSections: ({ folderVariantId, ...data }) => request(`/document/folder-variant/${folderVariantId}/section`, 'GET', data, { schema: [documentSectionSchema] }),
    postSectionClick: ({ sectionId, ...data }) => request(`/document/section/${sectionId}/track`, 'POST', data),

    reorderSectionsForAdmin: ({ folderVariantId, ...data }) => request(`/admin/document/folder-variant/${folderVariantId}/reorder`, 'PUT', data),

    getFoldersForAdmin: data => request('/admin/document/folder/', 'GET', data, { schema: [documentFolderSchema] }),
    getFolderForAdmin: ({ folderId, ...data }) => request(`/admin/document/folder/${folderId}`, 'GET', data, { schema: documentFolderSchema }),
    postFolderForAdmin: data => request('/admin/document/folder/', 'POST', data),
    putFolderForAdmin: ({ folderId, ...data }) => request(`/admin/document/folder/${folderId}`, 'PUT', data),
    deleteFolderVariantForAdmin: ({ folderVariantId, ...data }) => request(`/admin/document/folder-variant/${folderVariantId}`, 'DELETE', data),
    getSectionsForAdmin: ({ folderVariantId, ...data }) => request(`/admin/document/folder-variant/${folderVariantId}/section`, 'GET', data, { schema: [documentSectionSchema] }),
    getSectionForAdmin: ({ sectionId, ...data }) => request(`/admin/document/section/${sectionId}`, 'GET', data, { schema: documentSectionSchema }),
    postSectionForAdmin: ({ folderVariantId, ...data }) => request(`/admin/document/folder-variant/${folderVariantId}/section`, 'POST', data, { formData: true, schema: documentSectionSchema }),
    putSectionForAdmin: ({ sectionId, ...data }) => request(`/admin/document/section/${sectionId}`, 'POST', { ...data, _method: 'PUT' }, { formData: true, schema: documentSectionSchema }),
    deleteSectionForAdmin: ({ sectionId, ...data }) => request(`/admin/document/section/${sectionId}`, 'DELETE', data),
  },
});

const AUTH_USER_RECHECK_INTERVAL = 30 * 1000;
let lastAuthUserRecheck = 0;

const isAuthUserRecheckDue = () => {
  if (Date.now() - lastAuthUserRecheck < AUTH_USER_RECHECK_INTERVAL) {
    return false;
  }

  lastAuthUserRecheck = Date.now();

  return true;
};

export function* apiCall(apiFn, payload, config = {}) {
  const data = yield apiFn(payload);
  const { ok, response, error } = data;

  if (error?.type === 'FeatureLimitReached' || (error?.workspaceMismatch && isAuthUserRecheckDue())) {
    yield put(authActions.getAuthUser());
  }

  if (ok && config.onSuccess) {
    config.onSuccess(response);
  } else if (!ok && config.onFailure) {
    config.onFailure(error);
  }

  if (config.onComplete) {
    config.onComplete(data);
  }

  return data;
}

export function* commonApiSaga({ apiFn, workspaceId = false, successMessage = false, failureMessage, onFailure, onSuccess }, { type, payload }) {

  // For the future, maybe also trigger _started event?

  const extraPayload = {
    ...(workspaceId && { workspaceId: yield select(authSelectors.getWorkspaceId) }),
  };

  const { ok, response, error } = yield apiCall(apiFn, { ...extraPayload, ...payload });

  if (ok) {
    yield put({
      type: `${type}_SUCCESS`,
      payload: {
        ...(response?.data ? response : { data: response }), // Don't mess up plain array responses etc
        request: payload,
      },
    });

    if (successMessage !== false) {
      yield put(snackbarActions.createSuccess(successMessage));
    }

    if (onSuccess) {
      yield call(onSuccess);
    }
  } else {
    yield put({ type: `${type}_FAILURE`, payload: { ...error, request: payload } });

    if (failureMessage !== false) {
      yield put(snackbarActions.createFailure(failureMessage ?? error.message));
    }

    if (onFailure) {
      yield call(onFailure);
    }
  }

  // For the future, maybe also trigger _finished event?
}
